import { useClientPortalAPI, useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { Loader, SectionMessage } from "@certn/cascadia-react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { TemplateLayout } from "../templates/TemplateLayout";
import { CustomInstructionsFormSection } from "../templates/CustomInstructionsFormSection";
import { REFERENCE_DEFAULT_INSTRUCTIONS } from "../templates/customInstructionsConstants";
import { ReferenceCheckTemplateForm } from "./ReferenceCheckTemplateForm";
import {
  type ReferenceCheckTemplateFormValues,
  nameOnlyValidationSchema,
} from "./utils";

export function ReferenceCheckTemplateDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();

  const goBack = () => navigate("/settings/reference-check-templates");

  const { data, mutate, isLoading, error } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/reference/${templateId}/`
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/check-arguments-templates/reference/${templateId}/update/`,
    {
      showToastOnError: true,
      onSuccess: async () => {
        await mutate();
        showToast(t("Template updated successfully!"), { type: "success" });
        goBack();
      },
    }
  );

  const checkArguments = data?.check_arguments as {
    professional_quantity?: number;
    personal_quantity?: number;
    professional_questions?: string[];
    personal_questions?: string[];
    custom_instructions?: string;
  } | null;

  if (isLoading) {
    return <Loader overlay />;
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t("Unable to retrieve template.")}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  if (!data || !checkArguments) {
    return (
      <SectionMessage
        icon="warning"
        sectionTitle={t("Unable to retrieve template information")}
        type="danger"
      />
    );
  }

  return (
    <Formik<ReferenceCheckTemplateFormValues>
      enableReinitialize
      validateOnMount
      initialValues={{
        name: data.name,
        professional_quantity: checkArguments.professional_quantity ?? 1,
        personal_quantity: checkArguments.personal_quantity ?? 1,
        professional_questions: checkArguments.professional_questions ?? [],
        personal_questions: checkArguments.personal_questions ?? [],
        custom_instructions: checkArguments.custom_instructions ?? "",
      }}
      validationSchema={nameOnlyValidationSchema()}
      onSubmit={(values) =>
        submit({
          name: values.name,
          custom_instructions: values.custom_instructions || "",
        })
      }
    >
      {({ values, handleSubmit, isSubmitting, isValid, dirty }) => (
        <TemplateLayout
          hideCancelModal
          type="update"
          title={t("Reference check template details")}
          onComplete={handleSubmit}
          submitting={isSubmitting}
          onCancel={goBack}
          disableCompletion={!isValid || !dirty}
        >
          <ReferenceCheckTemplateForm values={values} isDisabled />
          <CustomInstructionsFormSection
            fieldName="custom_instructions"
            defaultInstructionsPlaceholder={REFERENCE_DEFAULT_INSTRUCTIONS()}
          />
        </TemplateLayout>
      )}
    </Formik>
  );
}
